import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

// material
import {
  Container,
  Stack,
  Button,
  Typography,
  Grid,
  Card,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
// components
import { LoadingButton } from '@mui/lab';
import { X } from 'lucide-react';
import Page from '../components/Page';
import { ProductCard } from '../sections/@dashboard/products';
// mock
import { getServices, updateResellingServices } from '../store/actions/services';
import { createResellingApiService, regenerateResellingServices } from '../api/services';
import ModalC from './components/CModal';

// ----------------------------------------------------------------------

export const groupBy = (key) => (array) =>
  array.reduce((objectsByKeyValue, obj) => {
    const value = obj[key];
    objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(obj);
    return objectsByKeyValue;
  }, {});

const PRODUCT_FILTER = {
  DATA: 'Data',
  AIRTIME: 'Airtime',
  ELECTRICITY: 'Electricity',
  CABLE: 'Cable TV',
  SCHOOL: 'School PIN',
};

export default function ResellingProducts() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const services = useSelector((state) => state.services.resellingServices);
  const userServices = useSelector((state) => state.services.services);
  const [currentTab, setCurrentTab] = useState(PRODUCT_FILTER.DATA);
  const [loading, setLoading] = React.useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [showAlertModel, setShowAlertModel] = useState(null);
  const [showAddAPIService, setShowAddApiService] = useState(false);

  // add api service config
  const [network, setNetwork] = useState('');
  const [networkProducts, setNetworkProducts] = useState([]);
  const [productToPurchase, setProductToPurchase] = useState(null);
  const [apiAmount, setApiAmount] = useState('');

  const data = services.filter((data) => data.service === 'Data');
  const dataServices = userServices.filter((data) => data.service === 'Data');

  const cable = services.filter((data) => data.service === 'Cable');
  const exam = services.filter((data) => data.service === 'ResultCheck');
  const electricity = services.filter((data) => data.service === 'Electricity');
  const airtime = services.filter((data) => data.service === 'Airtime');

  const groupByCategory = groupBy('category');

  const dataService = groupByCategory(data);

  const cableService = groupByCategory(cable);
  const examService = groupByCategory(exam);
  const electricityService = groupByCategory(electricity);
  const airtimeService = groupByCategory(airtime);

  const handleRegenerateServices = async (type) => {
    setLoading(type);
    const response = await regenerateResellingServices({ type: type.toLowerCase() });
    if (response.ok) {
      toast.success('Service updated successfully');
      setLoading(null);
      dispatch(updateResellingServices(response.data.data));
      setShowAlertModel(null);
      return;
    }

    setLoading(null);
    const error = response.data?.message || 'Error unable to complete your request';
    toast.error(error);
  };

  const handleCreateAPIService = async () => {
    if (!network) {
      toast.error('Please select network to create the api from');
      return;
    }

    if (!productToPurchase) {
      toast.error('Please select the service for the api to purchase from');
      return;
    }

    // validate amount
    if (Number.isNaN(apiAmount)) {
      toast.error('Invalid amount please check and try again');
      return;
    }

    setIsLoading(true);

    const response = await createResellingApiService({
      amount: apiAmount,
      service: productToPurchase,
    });

    if (response.ok) {
      setIsLoading(false);
      dispatch(getServices());
      toast.success('Service created successfully');
      setShowAddApiService(false);
      return;
    }

    const error = response.data?.message || 'Unable to create api service please try again';
    toast.error(error);
    setIsLoading(false);
  };

  const navigateToServiceCategory = (path, service) => {
    navigate(`${path}?currentService=${service}`);
  };

  React.useEffect(() => {
    dispatch(getServices());
  }, [dispatch]);

  React.useEffect(() => {
    setNetworkProducts((prev) =>
      dataServices?.filter((item) => item?.category?.toLowerCase()?.includes(network?.toLowerCase()))
    );
  }, [dataServices, network]);

  return (
    <Page title="Dashboard: update services">
      <Container>
        <Typography variant="h4" sx={{ mb: 1 }}>
          Service Management
        </Typography>
        <Typography>Manage your services by updating, adding and changing processors</Typography>
        <LoadingButton onClick={() => setShowAlertModel('All')} sx={{ mt: 2 }} variant="outlined">
          Regenerate Reselling Services
        </LoadingButton>
        <Grid container spacing={1} sx={{ mt: 2 }}>
          {['Data', 'Airtime', 'Electricity', 'Cable TV', 'School PIN'].map((item) => (
            <Grid key={item} item xs={6} sm={6} md={2}>
              <Card
                onClick={() => setCurrentTab(item)}
                sx={{
                  width: '100%',
                  height: '40px',
                  borderWidth: currentTab === item ? 1 : 0,
                  borderStyle: 'solid',
                  borderColor: 'blue',
                  cursor: 'pointer',
                }}
              >
                <Stack sx={{ width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center' }}>
                  <Typography>{item}</Typography>
                </Stack>
              </Card>
            </Grid>
          ))}
        </Grid>
        {PRODUCT_FILTER.DATA === currentTab && (
          <>
            <Typography variant="h6" sx={{ my: 2 }}>
              Manage Reselling Data Service
            </Typography>
            <Stack direction={'row'} spacing={1} sx={{ pb: 2 }}>
              <Button variant="outlined" onClick={() => setShowAddApiService(true)}>
                Create New API Plan
              </Button>
              <LoadingButton variant="outlined" onClick={() => setShowAlertModel('Data')}>
                Regenerate API Data Plans
              </LoadingButton>
            </Stack>
            <Grid container spacing={'10px'}>
              {Object.entries(dataService).map(([key, value]) => (
                <Grid key={key} item xs={6} sm={3} onClick={() => navigateToServiceCategory(key, value[0].service)}>
                  <ProductCard title={key} imageUrl={value[0].imageUrl} />
                </Grid>
              ))}
            </Grid>
          </>
        )}
        {PRODUCT_FILTER.AIRTIME === currentTab && (
          <>
            <Typography variant="h6" sx={{ my: 2 }}>
              Manage Reselling Airtime Service
            </Typography>

            <Grid container spacing={'10px'}>
              {Object.entries(airtimeService).map(([key, value]) => (
                <Grid key={key} item xs={6} sm={3} onClick={() => navigateToServiceCategory(key, value[0].service)}>
                  <ProductCard title={key} imageUrl={value[0].imageUrl} />
                </Grid>
              ))}
            </Grid>
          </>
        )}
        {PRODUCT_FILTER.ELECTRICITY === currentTab && (
          <>
            <Typography variant="h6" sx={{ my: 2 }}>
              Manage Reselling Electricity Service
            </Typography>

            <Grid container spacing={'10px'}>
              {Object.entries(electricityService).map(([key, value]) => (
                <Grid key={key} item xs={6} sm={3} onClick={() => navigateToServiceCategory(key, value[0].service)}>
                  <ProductCard title={key} imageUrl={value[0].imageUrl} />
                </Grid>
              ))}
            </Grid>
          </>
        )}
        {PRODUCT_FILTER.CABLE === currentTab && (
          <>
            <Typography variant="h6" sx={{ my: 2 }}>
              Manage Reselling TV Cable Service
            </Typography>

            <Grid container spacing={'10px'}>
              {Object.entries(cableService).map(([key, value]) => (
                <Grid key={key} item xs={6} sm={3} onClick={() => navigateToServiceCategory(key, value[0].service)}>
                  <ProductCard title={key} imageUrl={value[0].imageUrl} />
                </Grid>
              ))}
            </Grid>
          </>
        )}
        {PRODUCT_FILTER.SCHOOL === currentTab && (
          <>
            <Typography variant="h6" sx={{ my: 2 }}>
              Manage Reselling School Result Check Service
            </Typography>
            <Grid container spacing={'10px'}>
              {Object.entries(examService).map(([key, value]) => (
                <Grid key={key} item xs={6} sm={3} onClick={() => navigateToServiceCategory(key, value[0].service)}>
                  <ProductCard title={key} imageUrl={value[0].imageUrl} />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Container>

      {/* Create new api plan modal  */}
      <ModalC isOpen={showAddAPIService}>
        <Stack sx={{ flex: 1 }} spacing={2}>
          <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
            <Typography variant="h6" component={'h4'}>
              Create API Data Service
            </Typography>
            <IconButton onClick={() => setShowAddApiService(false)}>
              <X />
            </IconButton>
          </Stack>

          <Stack spacing={1}>
            <FormControl>
              <InputLabel>Network</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={network}
                label="Network"
                onChange={(e) => setNetwork(e.target.value)}
              >
                <MenuItem value={'mtn'}>MTN</MenuItem>
                <MenuItem value={'9mobile'}>9MOBILE</MenuItem>
                <MenuItem value={'airtel'}>AIRTEL</MenuItem>
                <MenuItem value={'glo'}>GLO</MenuItem>
                <MenuItem value={'smile'}>SMILE</MenuItem>
                <MenuItem value={'kirani'}>KIRANI</MenuItem>
                <MenuItem value={'alpha'}>ALPHA</MenuItem>
                <MenuItem value={'ratel'}>RATEL</MenuItem>
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel>Product to Purchase</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={productToPurchase}
                label="Network"
                onChange={(e) => setProductToPurchase(e.target.value)}
              >
                {networkProducts?.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {`${item.category} ${item?.details?.name} - ₦${item?.details?.amount}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              type="number"
              label="API Amount"
              value={apiAmount}
              onChange={(e) => setApiAmount(e.target.value)}
            />
            <LoadingButton
              disabled={!apiAmount || !productToPurchase || !network}
              loading={isLoading}
              variant={'contained'}
              onClick={handleCreateAPIService}
            >
              Create
            </LoadingButton>
            <Button variant="outlined">Cancel</Button>
          </Stack>
        </Stack>
      </ModalC>

      {/* Alert modal  */}
      <ModalC isOpen={showAlertModel}>
        <Stack spacing={2} sx={{ flex: 1, minHeight: 100, justifyContent: 'center', alignItems: 'center' }}>
          <Typography textAlign={'center'} variant="h6" component={'h4'}>
            {' '}
            {showAlertModel} Service Regenerate
          </Typography>
          <Typography textAlign={'center'} variant="body1">
            Are you sure you want to Regenerate {showAlertModel} API Services the system will remove {showAlertModel}{' '}
            api services and Regenerate them from your services
          </Typography>
          {loading ? (
            <CircularProgress />
          ) : (
            <Stack direction={'row'} spacing={2} alignItems={'center'}>
              <LoadingButton onClick={() => setShowAlertModel(null)} variant="outlined">
                Cancel
              </LoadingButton>
              <LoadingButton
                onClick={() => handleRegenerateServices(showAlertModel)}
                variant="contained"
                color={'error'}
              >
                Confirm
              </LoadingButton>
            </Stack>
          )}
        </Stack>
      </ModalC>
    </Page>
  );
}
